import { forwardRef, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Calendar, ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/utils/cn";

const POPOVER_W = 280;
const POPOVER_MARGIN = 8;

/* ─── Conversões ISO ↔ dd/MM/yyyy ───────────────────────────────────────── */

function isoToDisplay(iso: string): string {
  if (!iso) return "";
  const parts = iso.split("-");
  if (parts.length !== 3) return iso;
  const [y, m, d] = parts;
  return `${d}/${m}/${y}`;
}

function displayToISO(display: string): string {
  const digits = display.replace(/\D/g, "");
  if (digits.length < 8) return "";
  const d = digits.slice(0, 2);
  const m = digits.slice(2, 4);
  const y = digits.slice(4, 8);
  const date = new Date(`${y}-${m}-${d}`);
  if (isNaN(date.getTime())) return "";
  return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
}

function applyMask(raw: string): string {
  const digits = raw.replace(/\D/g, "").slice(0, 8);
  const d = digits.slice(0, 2);
  const m = digits.slice(2, 4);
  const y = digits.slice(4, 8);
  if (digits.length <= 2) return d;
  if (digits.length <= 4) return `${d}/${m}`;
  return `${d}/${m}/${y}`;
}

function isoToDate(iso: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  if (Number.isNaN(dt.getTime())) return null;
  return dt;
}

function dateToISO(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function sameMonth(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
}

function buildMonthGrid(baseMonth: Date): Date[] {
  const first = new Date(baseMonth.getFullYear(), baseMonth.getMonth(), 1);
  const start = new Date(first);
  start.setDate(first.getDate() - first.getDay());
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
}

function startOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/* ─── Props ──────────────────────────────────────────────────────────────── */

export interface DateInputProps {
  /** Valor em formato ISO: YYYY-MM-DD */
  value: string;
  /** Chamado com YYYY-MM-DD sempre que a data for válida e completa */
  onChange: (iso: string) => void;
  label?: string;
  error?: string;
  hint?: string;
  min?: string;
  max?: string;
  className?: string;
  id?: string;
  disabled?: boolean;
}

/* ─── Componente ─────────────────────────────────────────────────────────── */

export const DateInput = forwardRef<HTMLInputElement, DateInputProps>(
  ({ value, onChange, label, error, hint, min, max, className, id, disabled }, ref) => {
    const inputId = id ?? label?.toLowerCase().replace(/\s+/g, "-") ?? "date-input";

    const [display, setDisplay] = useState(() => isoToDisplay(value));
    const prevIso = useRef(value);
    const textInputRef = useRef<HTMLInputElement | null>(null);
    const rootRef = useRef<HTMLDivElement | null>(null);
    const popoverRef = useRef<HTMLDivElement | null>(null);
    const [calendarOpen, setCalendarOpen] = useState(false);
    const [popoverPos, setPopoverPos] = useState({ top: 0, left: 0 });
    const [viewDate, setViewDate] = useState(() => isoToDate(value) ?? new Date());
    const minDate = useMemo(() => (min ? isoToDate(min) : null), [min]);
    const maxDate = useMemo(() => (max ? isoToDate(max) : null), [max]);

    const setTextInputRef = (el: HTMLInputElement | null) => {
      textInputRef.current = el;
      if (typeof ref === "function") ref(el);
      else if (ref) ref.current = el;
    };

    /* Sincroniza display quando o valor ISO mudar externamente */
    useEffect(() => {
      if (value !== prevIso.current) {
        prevIso.current = value;
        setDisplay(isoToDisplay(value));
        const parsed = isoToDate(value);
        if (parsed) setViewDate(parsed);
      }
    }, [value]);

    useLayoutEffect(() => {
      if (!calendarOpen) return;
      const updatePopoverPosition = () => {
        const wrap = rootRef.current;
        if (!wrap) return;
        const rect = wrap.getBoundingClientRect();
        let left = rect.left;
        if (left + POPOVER_W > window.innerWidth - POPOVER_MARGIN) {
          left = window.innerWidth - POPOVER_W - POPOVER_MARGIN;
        }
        if (left < POPOVER_MARGIN) left = POPOVER_MARGIN;
        setPopoverPos({ top: rect.bottom + POPOVER_MARGIN, left });
      };
      updatePopoverPosition();
      const onScrollOrResize = () => updatePopoverPosition();
      window.addEventListener("resize", onScrollOrResize);
      window.addEventListener("scroll", onScrollOrResize, true);
      return () => {
        window.removeEventListener("resize", onScrollOrResize);
        window.removeEventListener("scroll", onScrollOrResize, true);
      };
    }, [calendarOpen]);

    useEffect(() => {
      const closeOnOutside = (ev: MouseEvent) => {
        const t = ev.target as Node;
        if (rootRef.current?.contains(t)) return;
        if (popoverRef.current?.contains(t)) return;
        setCalendarOpen(false);
      };
      document.addEventListener("mousedown", closeOnOutside);
      return () => document.removeEventListener("mousedown", closeOnOutside);
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const masked = applyMask(e.target.value);
      setDisplay(masked);

      const iso = displayToISO(masked);
      if (iso) {
        prevIso.current = iso;
        onChange(iso);
        const parsed = isoToDate(iso);
        if (parsed) setViewDate(parsed);
      }
    };

    const handleBlur = () => {
      /* Se o campo não formou data válida, volta pro valor anterior */
      const iso = displayToISO(display);
      if (!iso && value) {
        setDisplay(isoToDisplay(value));
      }
    };

    const selectDate = (date: Date) => {
      const iso = dateToISO(date);
      prevIso.current = iso;
      setDisplay(isoToDisplay(iso));
      onChange(iso);
      setViewDate(date);
      setCalendarOpen(false);
    };

    const isOutOfRange = (date: Date): boolean => {
      const day = startOfLocalDay(date);
      if (minDate && day < startOfLocalDay(minDate)) return true;
      if (maxDate && day > startOfLocalDay(maxDate)) return true;
      return false;
    };

    /* min/max para feedback visual apenas (não bloqueia digitação) */
    const minDisplay = min ? isoToDisplay(min) : undefined;
    const maxDisplay = max ? isoToDisplay(max) : undefined;

    return (
      <div className="flex flex-col gap-1.5">
        {label && (
          <label
            htmlFor={inputId}
            className="text-xs font-semibold text-slate-500 uppercase tracking-wide"
          >
            {label}
          </label>
        )}

        <div className="relative" ref={rootRef}>
          <input
            ref={setTextInputRef}
            id={inputId}
            type="text"
            inputMode="numeric"
            value={display}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="dd/mm/aaaa"
            disabled={disabled}
            aria-label={label}
            onFocus={() => setCalendarOpen(true)}
            title={
              [
                minDisplay ? `Mínimo: ${minDisplay}` : "",
                maxDisplay ? `Máximo: ${maxDisplay}` : "",
              ]
                .filter(Boolean)
                .join(" — ") || undefined
            }
            className={cn(
              "w-full h-10 rounded-lg border bg-slate-50 pl-3 pr-9 text-sm text-slate-800",
              "placeholder:text-slate-400 transition-colors font-mono tracking-wide",
              "focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-0 focus:border-brand-600",
              error
                ? "border-red-400 focus:ring-red-400"
                : "border-slate-200 hover:border-slate-300",
              disabled && "opacity-60 cursor-not-allowed",
              className
            )}
          />

          <button
            type="button"
            onClick={() => setCalendarOpen((v) => !v)}
            disabled={disabled}
            aria-label={label ? `Abrir calendário de ${label}` : "Abrir calendário"}
            className={cn(
              "absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-slate-400 transition-colors",
              "hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus:ring-2 focus:ring-brand-600",
              disabled && "cursor-not-allowed opacity-60"
            )}
          >
            <Calendar className="w-4 h-4" />
          </button>

          {calendarOpen &&
            !disabled &&
            createPortal(
              <div
                ref={popoverRef}
                role="dialog"
                aria-label={label ? `Calendário — ${label}` : "Calendário"}
                className="fixed z-[1000] w-[280px] rounded-xl border border-slate-200 bg-white p-3 shadow-2xl"
                style={{ top: popoverPos.top, left: popoverPos.left }}
              >
                <div className="mb-3 flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => setViewDate((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1))}
                    className="rounded-md p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700"
                    aria-label="Mês anterior"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span className="text-sm font-semibold capitalize text-slate-800">
                    {viewDate.toLocaleDateString("pt-BR", { month: "long", year: "numeric" })}
                  </span>
                  <button
                    type="button"
                    onClick={() => setViewDate((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1))}
                    className="rounded-md p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700"
                    aria-label="Próximo mês"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>

                <div className="mb-1 grid grid-cols-7 text-center text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  {["D", "S", "T", "Q", "Q", "S", "S"].map((day, i) => (
                    <span key={`${day}-${i}`} className="py-1">
                      {day}
                    </span>
                  ))}
                </div>

                <div className="grid grid-cols-7 gap-1">
                  {buildMonthGrid(viewDate).map((date) => {
                    const selected = value ? sameDay(date, isoToDate(value) ?? date) : false;
                    const today = sameDay(date, new Date());
                    const muted = !sameMonth(date, viewDate);
                    const blocked = isOutOfRange(date);
                    return (
                      <button
                        key={dateToISO(date)}
                        type="button"
                        disabled={blocked}
                        onClick={() => selectDate(date)}
                        className={cn(
                          "h-8 rounded-md text-sm tabular-nums transition-colors",
                          selected && "bg-brand-600 font-semibold text-white hover:bg-brand-700",
                          !selected && !blocked && "text-slate-700 hover:bg-slate-100",
                          muted && !selected && "text-slate-300",
                          today && !selected && "border border-brand-300",
                          blocked && "cursor-not-allowed text-slate-300"
                        )}
                      >
                        {date.getDate()}
                      </button>
                    );
                  })}
                </div>

                <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-2">
                  <button
                    type="button"
                    onClick={() => setCalendarOpen(false)}
                    className="text-xs font-medium text-slate-500 hover:text-slate-700"
                  >
                    Fechar
                  </button>
                  <button
                    type="button"
                    onClick={() => selectDate(new Date())}
                    className="text-xs font-semibold text-brand-600 hover:text-brand-700"
                    disabled={isOutOfRange(new Date())}
                  >
                    Hoje
                  </button>
                </div>
              </div>,
              document.body
            )}
        </div>

        {error && <p className="text-xs text-red-500 font-medium">{error}</p>}
        {hint && !error && <p className="text-xs text-slate-400">{hint}</p>}
      </div>
    );
  }
);

DateInput.displayName = "DateInput";
